import { useEffect, useState, useRef } from 'react';
import { Badge, Btn, Card, Alert, SectionHeader, DataTable, FormRow, Field, Modal, FormActions, FileUpload } from '../../components/ui';
import { Icon } from '../../icons';
import api from '../../api/axios';
import { toast } from 'react-hot-toast';
import { formatDateByPreference } from '../../utils/formatters';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

const ChipSVG = ({ color = '#d4b96a' }) => (
  <svg width={36} height={28} viewBox="0 0 36 28" fill="none">
    <rect x="1" y="1" width="34" height="26" rx="4" fill={color} stroke={color} strokeOpacity="0.3" />
    <rect x="1" y="1" width="34" height="26" rx="4" fill="white" opacity="0.08" />
    <rect x="5" y="5" width="10" height="7" rx="1" fill="rgba(0,0,0,0.25)" />
    <rect x="21" y="5" width="10" height="7" rx="1" fill="rgba(0,0,0,0.25)" />
    <rect x="5" y="16" width="10" height="7" rx="1" fill="rgba(0,0,0,0.25)" />
    <rect x="21" y="16" width="10" height="7" rx="1" fill="rgba(0,0,0,0.25)" />
    <rect x="13" y="9" width="10" height="10" rx="1" fill="rgba(0,0,0,0.15)" />
  </svg>
);

const ACCENT_CLASSES = {
  blue: {
    card: 'bg-gradient-to-br from-[#0c1b6e] via-[#1a3290] to-[#0a1550] shadow-[0_20px_60px_rgba(79,114,246,0.45),0_4px_16px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.07),inset_0_-1px_0_rgba(0,0,0,0.2)]',
    accent: 'text-[#7ca3ff]',
    chip: '#d4b96a',
  },
  green: {
    card: 'bg-gradient-to-br from-[#0a3728] via-[#0d5c3f] to-[#072a1e] shadow-[0_20px_60px_rgba(16,147,100,0.4),0_4px_16px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.07),inset_0_-1px_0_rgba(0,0,0,0.2)]',
    accent: 'text-[#4ade9a]',
    chip: '#c8a84b',
  },
  amber: {
    card: 'bg-gradient-to-br from-[#5c2d0a] via-[#7a3f0e] to-[#421f06] shadow-[0_20px_60px_rgba(200,120,30,0.4),0_4px_16px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.07),inset_0_-1px_0_rgba(0,0,0,0.2)]',
    accent: 'text-[#fbbf24]',
    chip: '#e8cb78',
  },
  purple: {
    card: 'bg-gradient-to-br from-[#3c0a5c] via-[#5e0d7a] to-[#230642] shadow-[0_20px_60px_rgba(120,30,200,0.4),0_4px_16px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.07),inset_0_-1px_0_rgba(0,0,0,0.2)]',
    accent: 'text-[#a855f7]',
    chip: '#e8cb78',
  },
};

const defaultForm = {
  name: '',
  bank_name: '',
  account_number: '',
  account_type: 'savings',
  branch: '',
  ifsc_code: '',
  current_balance: '0',
  is_primary: false,
  card_accent: 'blue',
};

export default function BankScreen() {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.bank?.[key] || translations.en?.bank?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [visibleBalanceId, setVisibleBalanceId] = useState(null);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [accountErrors, setAccountErrors] = useState({});
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [stmtModal, setStmtModal] = useState(null);
  const [stmtForm, setStmtForm] = useState({ files: [], password: '' });
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtErrors, setStmtErrors] = useState({});
  const [paymentModal, setPaymentModal] = useState(null);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ type: 'UPI', identifier: '', status: 'active', bank_account_id: '' });
  const [txModal, setTxModal] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [stmtsModal, setStmtsModal] = useState(null);
  const [statements, setStatements] = useState([]);
  const [stmtsLoading, setStmtsLoading] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [fdDetailModal, setFdDetailModal] = useState(null);
  const [importError, setImportError] = useState(false);
  const trackedProcessingIdsRef = useRef(new Set());

  const sf = key => e => {
    const val = e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm(current => ({ ...current, [key]: val }));
    if (accountErrors[key]) {
      setAccountErrors(current => ({ ...current, [key]: null }));
    }
  };

  const handleAccountNumberChange = e => {
    const val = (e.target ? e.target.value : e).replace(/\D/g, '').slice(0, 20);
    setForm(current => ({ ...current, account_number: val }));
    if (accountErrors.account_number) {
      setAccountErrors(current => ({ ...current, account_number: null }));
    }
  };

  const handleIfscChange = e => {
    let raw = (e.target ? e.target.value : e) || '';
    raw = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');

    setForm(current => {
      let val = raw;
      const prev = current.ifsc_code || '';
      // If previous value had letters, and input starts with digits (e.g. mobile keyboard wiped previous text on mode switch)
      if (prev && /^[A-Z]+$/.test(prev) && /^\d+$/.test(raw)) {
        val = prev + raw;
      }
      val = val.slice(0, 11);
      return { ...current, ifsc_code: val };
    });

    if (accountErrors.ifsc_code) {
      setAccountErrors(current => ({ ...current, ifsc_code: null }));
    }
  };

  const handleBalanceChange = e => {
    let val = e.target ? e.target.value : e;
    if (val < 0) val = '';
    setForm(current => ({ ...current, current_balance: val }));
    if (accountErrors.current_balance) {
      setAccountErrors(current => ({ ...current, current_balance: null }));
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/bank/accounts');
      setAccounts(res.data);
    } catch {
      toast.error(t("toast_load_accounts_failed"));
    } finally {
      setLoading(false);
    }
  };

  const maskBalance = amount => {
    const formatted = parseFloat(amount || 0).toLocaleString('en-IN');
    return `Rs. ${'*'.repeat(formatted.length)}`;
  };

  const resetAccountModal = () => {
    setAccountModalOpen(false);
    setEditingAccount(null);
    setForm(defaultForm);
    setAccountErrors({});
  };

  const openCreateModal = () => {
    setEditingAccount(null);
    setForm(defaultForm);
    setAccountErrors({});
    setAccountModalOpen(true);
  };

  const openEditModal = account => {
    setEditingAccount(account);
    setForm({
      name: account.name || '',
      bank_name: account.bank_name || '',
      account_number: account.account_number || '',
      account_type: account.account_type || 'savings',
      branch: account.branch || '',
      ifsc_code: account.ifsc_code || '',
      current_balance: String(account.current_balance ?? '0'),
      is_primary: !!account.is_primary,
      card_accent: account.card_accent || 'blue',
    });
    setAccountErrors({});
    setAccountModalOpen(true);
  };

  const validateAccountForm = () => {
    const newErrors = {};

    // 1. Display Name
    const nameTrim = form.name?.trim();
    if (!nameTrim) {
      newErrors.name = t("val_name_required");
    } else if (nameTrim.length < 2) {
      newErrors.name = t("val_name_min");
    } else if (nameTrim.length > 255) {
      newErrors.name = t("val_name_max");
    } else if (!/[a-zA-Z]/.test(nameTrim)) {
      newErrors.name = t("val_name_letters") || "Display name must contain at least one letter";
    }

    // 2. Bank Name
    const bankTrim = form.bank_name?.trim();
    if (!bankTrim) {
      newErrors.bank_name = t("val_bank_name_required");
    } else if (bankTrim.length < 2) {
      newErrors.bank_name = t("val_bank_name_min");
    } else if (bankTrim.length > 255) {
      newErrors.bank_name = t("val_bank_name_max");
    } else if (!/[a-zA-Z]/.test(bankTrim)) {
      newErrors.bank_name = t("val_bank_name_letters") || "Bank name must contain at least one letter";
    }

    // 3. Account Type
    if (!['savings', 'current', 'fd'].includes(form.account_type)) {
      newErrors.account_type = t("val_account_type_invalid") || "Please select a valid account type";
    }

    // 4. Account Number
    const accNum = form.account_number?.trim();
    if (!accNum) {
      newErrors.account_number = t("val_account_number_required");
    } else if (!/^\d{8,20}$/.test(accNum)) {
      newErrors.account_number = t("val_account_number_invalid");
    } else if (/^0+$/.test(accNum)) {
      newErrors.account_number = t("val_account_number_zero") || "Account number cannot be all zeros";
    } else {
      const isDuplicate = accounts.some(
        acc => String(acc.account_number).trim() === accNum && acc.id !== editingAccount?.id
      );
      if (isDuplicate) {
        newErrors.account_number = t("val_account_number_exists") || "A bank account with this account number already exists";
      }
    }

    // 5. IFSC Code
    const ifsc = form.ifsc_code?.trim().toUpperCase();
    if (!ifsc) {
      newErrors.ifsc_code = t("val_ifsc_required") || "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      newErrors.ifsc_code = t("val_ifsc_invalid");
    }

    // 6. Branch (optional)
    const branchTrim = form.branch?.trim();
    if (branchTrim && branchTrim.length < 2) {
      newErrors.branch = t("val_branch_min") || "Branch must be at least 2 characters";
    } else if (branchTrim && branchTrim.length > 255) {
      newErrors.branch = t("val_branch_max") || "Branch cannot exceed 255 characters";
    }

    // 7. Opening Balance
    const balanceStr = String(form.current_balance ?? '').trim();
    if (balanceStr === '') {
      newErrors.current_balance = t("val_opening_balance_required");
    } else if (isNaN(Number(balanceStr)) || Number(balanceStr) < 0) {
      newErrors.current_balance = t("val_opening_balance_positive");
    } else if (!/^\d+(\.\d{1,2})?$/.test(balanceStr)) {
      newErrors.current_balance = t("val_opening_balance_decimals") || "Opening balance can have at most 2 decimal places";
    } else if (Number(balanceStr) > 999999999999.99) {
      newErrors.current_balance = t("val_opening_balance_max") || "Opening balance cannot exceed ₹99,999 Crore";
    }

    // 8. Card Accent
    if (!['blue', 'green', 'amber', 'purple'].includes(form.card_accent)) {
      newErrors.card_accent = "Please select a valid card color";
    }

    setAccountErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(t("toast_form_validation_failed"));
      return false;
    }
    return true;
  };

  const saveAccount = async () => {
    if (!validateAccountForm()) return;
    setAccountSubmitting(true);
    try {
      await api.post('/api/v1/bank/accounts', {
        ...form,
        name: form.name.trim(),
        bank_name: form.bank_name.trim(),
        account_number: form.account_number.trim(),
        ifsc_code: form.ifsc_code ? form.ifsc_code.trim().toUpperCase() : '',
        branch: form.branch ? form.branch.trim() : '',
      });
      toast.success(t("toast_add_account_success"));
      resetAccountModal();
      fetchAccounts();
    } catch (error) {
      if (error.errors) {
        const backendErrs = {};
        Object.keys(error.errors).forEach(k => {
          backendErrs[k] = Array.isArray(error.errors[k]) ? error.errors[k][0] : error.errors[k];
        });
        setAccountErrors(backendErrs);
        toast.error(t("toast_form_validation_failed"));
      } else {
        toast.error(error.message || t("toast_add_account_failed"));
      }
    } finally {
      setAccountSubmitting(false);
    }
  };

  const updateAccount = async () => {
    if (!validateAccountForm()) return;
    setAccountSubmitting(true);
    try {
      await api.patch(`/api/v1/bank/accounts/${editingAccount.id}`, {
        ...form,
        name: form.name.trim(),
        bank_name: form.bank_name.trim(),
        account_number: form.account_number.trim(),
        ifsc_code: form.ifsc_code ? form.ifsc_code.trim().toUpperCase() : '',
        branch: form.branch ? form.branch.trim() : '',
      });
      toast.success(t("toast_update_account_success"));
      resetAccountModal();
      fetchAccounts();
    } catch (error) {
      if (error.errors) {
        const backendErrs = {};
        Object.keys(error.errors).forEach(k => {
          backendErrs[k] = Array.isArray(error.errors[k]) ? error.errors[k][0] : error.errors[k];
        });
        setAccountErrors(backendErrs);
        toast.error(t("toast_form_validation_failed"));
      } else {
        toast.error(error.message || t("toast_update_account_failed"));
      }
    } finally {
      setAccountSubmitting(false);
    }
  };

  useEffect(() => {
    if (!stmtsModal?.id) {
      trackedProcessingIdsRef.current = new Set();
      setImportError(false);
      return;
    }

    const processing = (statements || []).filter(
      s => s.status === 'processing' || s.status === 'pending'
    );

    if (processing.length === 0) return;

    processing.forEach(s => trackedProcessingIdsRef.current.add(String(s.id)));

    if (importError) return;

    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/api/v1/bank/accounts/${stmtsModal.id}/statements`);
        const updated = res.data.data || res.data || [];
        setStatements(updated);

        // Check if any tracked statement encountered an error in import
        const failedStmt = updated.find(
          s => trackedProcessingIdsRef.current.has(String(s.id)) && (s.status === 'failed' || Boolean(s.error_message))
        );

        if (failedStmt) {
          setImportError(true);
          toast.error(failedStmt.error_message || t("toast_statement_import_failed"));
          return;
        }

        // Check if all tracked statements finished processing
        const stillProcessing = updated.some(
          s => trackedProcessingIdsRef.current.has(String(s.id)) && (s.status === 'processing' || s.status === 'pending')
        );

        if (!stillProcessing) {
          trackedProcessingIdsRef.current = new Set();
          toast.success(t("toast_statement_import_completed"));
          fetchAccounts();
        }
      } catch (error) {
        // Stop if there is any error in the import process
        setImportError(true);
        toast.error(error.response?.data?.message || t("toast_load_statements_failed"));
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [stmtsModal?.id, statements, importError]);

  const handleUploadStatement = async () => {
    if (!stmtForm.files.length) return;
    const formData = new FormData();
    formData.append('statement', stmtForm.files[0]);
    if (stmtForm.password) formData.append('password', stmtForm.password);

    try {
      setStmtLoading(true);
      const res = await api.post(`/api/v1/bank/accounts/${stmtModal.id}/statements`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(res.data.message || t("toast_upload_statement_success"));

      await fetchAccounts();
      const currentAccount = stmtModal;
      setStmtModal(null);
      setStmtForm({ files: [], password: '' });
      setStmtErrors({});
      setImportError(false);
      fetchStatements(currentAccount);
    } catch (error) {
      setStmtErrors(error.response?.data?.errors || {});
      toast.error(error.response?.data?.message || t("toast_upload_statement_failed"));
    } finally {
      setStmtLoading(false);
    }
  };

  const fetchStatements = async account => {
    setStmtsModal(account);
    setStmtsLoading(true);
    setImportError(false);
    try {
      const res = await api.get(`/api/v1/bank/accounts/${account.id}/statements`);
      setStatements(res.data.data || res.data || []);
    } catch {
      toast.error(t("toast_load_statements_failed") || "Failed to load statements");
    } finally {
      setStmtsLoading(false);
    }
  };

  const fetchTransactions = async account => {
    setTxModal(account);
    setTxLoading(true);
    try {
      const res = await api.get(`/api/v1/bank/accounts/${account.id}/transactions`);
      setTransactions(res.data);
    } catch {
      toast.error(t("toast_load_transactions_failed"));
    } finally {
      setTxLoading(false);
    }
  };

  const resetPaymentModal = () => {
    setPaymentModal(null);
    setEditingPaymentMethod(null);
    setPaymentForm({ type: 'UPI', identifier: '', status: 'active', bank_account_id: '' });
  };

  const openLinkMethodModal = account => {
    setEditingPaymentMethod(null);
    setPaymentForm({
      type: 'UPI',
      identifier: '',
      status: 'active',
      bank_account_id: account?.id || '',
    });
    setPaymentModal(account);
  };

  const openEditPaymentModal = (method, account) => {
    setEditingPaymentMethod(method);
    setPaymentForm({
      type: method.type || 'UPI',
      identifier: method.identifier || '',
      status: method.status || 'active',
      bank_account_id: method.bank_account_id || account?.id || '',
    });
    setPaymentModal(account);
  };

  const savePaymentMethod = async () => {
    try {
      if (editingPaymentMethod) {
        await api.patch(`/api/v1/bank/payment-methods/${editingPaymentMethod.id}`, paymentForm);
        toast.success(t("toast_update_payment_success"));
      } else {
        const targetAccountId = paymentForm.bank_account_id || paymentModal?.id;
        await api.post(`/api/v1/bank/accounts/${targetAccountId}/payment-methods`, paymentForm);
        toast.success(t("toast_link_payment_success"));
      }
      resetPaymentModal();
      fetchAccounts();
    } catch (error) {
      const msg = error.response?.data?.message || (editingPaymentMethod ? t("toast_update_payment_failed") : t("toast_link_payment_failed"));
      toast.error(msg);
    }
  };

  const deletePaymentMethod = async method => {
    const confirmed = window.confirm(
      t("confirm_delete_payment_method", { identifier: method.identifier })
    );

    if (!confirmed) return;

    try {
      await api.delete(`/api/v1/bank/payment-methods/${method.id}`);
      toast.success(t("toast_delete_payment_success"));
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || t("toast_delete_payment_failed"));
    }
  };

  const deleteAccount = account => {
    setDeleteAccountModal(account);
  };

  const confirmDeleteAccount = async () => {
    if (!deleteAccountModal) return;
    setDeletingAccount(true);
    try {
      await api.delete(`/api/v1/bank/accounts/${deleteAccountModal.id}`);
      toast.success(t("toast_delete_account_success"));
      if (visibleBalanceId === deleteAccountModal.id) setVisibleBalanceId(null);
      if (stmtModal?.id === deleteAccountModal.id) setStmtModal(null);
      if (paymentModal?.id === deleteAccountModal.id) resetPaymentModal();
      if (txModal?.id === deleteAccountModal.id) setTxModal(null);
      if (stmtsModal?.id === deleteAccountModal.id) setStmtsModal(null);
      if (fdDetailModal?.id === deleteAccountModal.id) setFdDetailModal(null);
      setDeleteAccountModal(null);
      await fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || t("toast_delete_account_failed"));
    } finally {
      setDeletingAccount(false);
    }
  };

  if (loading && accounts.length === 0) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" /></div>;
  }

  return (
    <div className="fade-up space-y-5">
      {accountModalOpen && (
        <Modal title={editingAccount ? t("title_edit_bank_account") : t("title_add_bank_account")} onClose={resetAccountModal}>
          <Alert type="warn">{t("alert_encrypted_data")}</Alert>
          <FormRow cols={2}>
            <Field 
              label={t("label_display_name")} 
              value={form.name} 
              onChange={sf('name')} 
              required 
              placeholder={t("placeholder_display_name")} 
              maxLength={255}
              error={accountErrors.name}
            />
            <Field 
              label={t("label_bank_name")} 
              value={form.bank_name} 
              onChange={sf('bank_name')} 
              required 
              placeholder={t("placeholder_bank_name")} 
              maxLength={255}
              error={accountErrors.bank_name}
            />
          </FormRow>
          <FormRow cols={2}>
            <Field 
              label={t("label_account_type")} 
              type="select" 
              value={form.account_type} 
              onChange={sf('account_type')} 
              required
              options={[{ label: t("type_savings"), value: "savings" }, { label: t("type_current"), value: "current" }, { label: t("type_fd"), value: "fd" }]} 
              error={accountErrors.account_type}
            />
            <Field 
              label={t("label_card_color")} 
              type="select" 
              value={form.card_accent} 
              onChange={sf('card_accent')} 
              options={[{ label: t("color_blue"), value: "blue" }, { label: t("color_green"), value: "green" }, { label: t("color_amber"), value: "amber" }, { label: t("color_purple"), value: "purple" }]} 
              error={accountErrors.card_accent}
            />
          </FormRow>
          <FormRow cols={2}>
            <Field 
              label={t("label_account_number")} 
              value={form.account_number} 
              onChange={handleAccountNumberChange} 
              required 
              placeholder="e.g., 123456789012"
              maxLength={20}
              error={accountErrors.account_number}
            />
            <Field 
              label={t("label_ifsc_code")} 
              value={form.ifsc_code} 
              onChange={handleIfscChange} 
              required
              placeholder="e.g., HDFC0001234"
              maxLength={11}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="off"
              style={{ textTransform: "uppercase" }}
              className="uppercase font-mono"
              error={accountErrors.ifsc_code}
            />
          </FormRow>
          <FormRow cols={2}>
            <Field 
              label={t("label_branch")} 
              value={form.branch} 
              onChange={sf('branch')} 
              placeholder="e.g., Mumbai Central"
              maxLength={255}
              error={accountErrors.branch}
            />
            <Field 
              label={t("label_opening_balance_rs")} 
              type="number" 
              value={form.current_balance} 
              onChange={handleBalanceChange} 
              required
              min="0"
              step="0.01"
              placeholder="0.00"
              error={accountErrors.current_balance}
            />
          </FormRow>
          <div className="mb-4 flex items-center gap-2 px-1">
            <input type="checkbox" id="is_primary" checked={form.is_primary} onChange={sf('is_primary')} className="h-4 w-4 rounded text-primary focus:ring-primary" />
            <label htmlFor="is_primary" className="text-sm font-medium text-ink">{t("label_set_primary")}</label>
          </div>
          <FormActions 
            onSave={editingAccount ? updateAccount : saveAccount} 
            onCancel={resetAccountModal} 
            label={editingAccount ? t("btn_update_account") : t("btn_add_account")} 
            loading={accountSubmitting}
          />
        </Modal>
      )}

      {stmtModal && (
        <Modal title={t("title_upload_statement", { name: stmtModal.name })} onClose={() => { setStmtModal(null); setStmtErrors({}); setStmtForm({ files: [], password: '' }); }}>
          <div className="mt-4">
            <FileUpload
              label={t("label_bank_statement_file")}
              accept=".pdf,.xls,.xlsx,.csv"
              files={stmtForm.files}
              onChange={files => { setStmtForm(current => ({ ...current, files })); if (stmtErrors.statement) setStmtErrors(current => ({ ...current, statement: null })); }}
              note={t("note_upload_statement_formats")}
              required
            />
            {stmtErrors.statement && <p className="mt-1 text-xs font-medium text-red-500">{stmtErrors.statement?.[0] || stmtErrors.statement}</p>}
          </div>
          {/* <Field label="PDF Password (Optional)" type="password" value={stmtForm.password} onChange={e => setStmtForm(current => ({ ...current, password: e.target.value }))} placeholder="If your PDF is protected" /> */}
          <FormActions onSave={handleUploadStatement} onCancel={() => setStmtModal(null)} label={t("btn_upload_parse")} disabled={!stmtForm.files.length} loading={stmtLoading} />
        </Modal>
      )}

      {txModal && (
        <Modal title={t("title_transactions", { name: txModal.name })} onClose={() => setTxModal(null)} xl bodyClassName="!p-0">
          {txLoading ? (
            <div className="py-10 text-center text-muted">{t("msg_loading_transactions")}</div>
          ) : transactions.length === 0 ? (
            <div className="py-10 text-center text-muted">{t("msg_no_transactions")}</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              <DataTable
                cols={[t("col_date"), t("col_description"), t("col_ref"), t("col_withdrawal"), t("col_deposit"), t("col_balance")]}
                horizontalScroll={true}
                rows={transactions.map(t => [
                  formatDateByPreference(t.transaction_date),
                  <span className="break-words text-xs whitespace-normal">{t.description}</span>,
                  <span className="font-mono text-xs text-muted">{t.reference_number || '-'}</span>,
                  <span className="font-semibold text-red-500">{t.withdrawal > 0 ? `Rs. ${parseFloat(t.withdrawal).toLocaleString('en-IN')}` : '-'}</span>,
                  <span className="font-semibold text-emerald-600">{t.deposit > 0 ? `Rs. ${parseFloat(t.deposit).toLocaleString('en-IN')}` : '-'}</span>,
                  `Rs. ${parseFloat(t.closing_balance || 0).toLocaleString('en-IN')}`,
                ])}
              />
            </div>
          )}
        </Modal>
      )}

      {stmtsModal && (
        <Modal title={`Statements - ${stmtsModal.name}`} onClose={() => setStmtsModal(null)} xl bodyClassName="!p-0">
          <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
             <Btn variant="outline" sm onClick={() => fetchStatements(stmtsModal)} icon="refreshCw" loading={stmtsLoading}>Refresh</Btn>
             {statements.some(s => s.status === 'processing' || s.status === 'pending') && !importError && (
               <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-medium">
                 <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                 Importing statement...
               </span>
             )}
          </div>
          {stmtsLoading && statements.length === 0 ? (
            <div className="py-10 text-center text-muted">Loading statements...</div>
          ) : statements.length === 0 ? (
            <div className="py-10 text-center text-muted">No statements found</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              <DataTable
                cols={["Date Uploaded", "File Name", "Status", "Errors"]}
                rows={statements.map(s => [
                  formatDateByPreference(s.created_at),
                  <span className="font-semibold text-sm">{s.file_name}</span>,
                  <Badge color={s.status === 'completed' ? 'green' : s.status === 'failed' ? 'red' : 'yellow'}>
                    {s.status === 'processing' ? 'Processing...' : s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : 'Unknown'}
                  </Badge>,
                  <span className="text-xs text-red-500 whitespace-normal break-words max-w-xs">{s.error_message || '-'}</span>,
                ])}
              />
            </div>
          )}
        </Modal>
      )}

      {paymentModal && (
        <Modal 
          title={editingPaymentMethod ? t("title_edit_payment_method") : t("title_link_method", { name: paymentModal.name })} 
          onClose={resetPaymentModal}
        >
          <Field 
            label={t("label_method_type")} 
            type="select" 
            value={paymentForm.type} 
            onChange={e => setPaymentForm(current => ({ ...current, type: e.target.value }))} 
            options={[
              { label: t("method_upi"), value: "UPI" },
              { label: t("method_netbanking"), value: "Netbanking" },
              { label: t("method_debit_card"), value: "Debit Card" }
            ]} 
          />
          <div className='my-3'></div>
          <Field 
            label={t("label_identifier")} 
            value={paymentForm.identifier} 
            onChange={e => setPaymentForm(current => ({ ...current, identifier: e.target.value }))} 
            required 
            placeholder={t("placeholder_identifier")} 
          />
          {editingPaymentMethod && (
            <>
              <div className='my-3'></div>
              <Field 
                label={t("col_status")} 
                type="select" 
                value={paymentForm.status} 
                onChange={e => setPaymentForm(current => ({ ...current, status: e.target.value }))} 
                options={[
                  { label: t("status_active"), value: "active" },
                  { label: t("status_inactive"), value: "inactive" }
                ]} 
              />
              <div className='my-3'></div>
              <Field 
                label={t("col_linked_account")} 
                type="select" 
                value={paymentForm.bank_account_id} 
                onChange={e => setPaymentForm(current => ({ ...current, bank_account_id: e.target.value }))} 
                options={accounts.map(acc => ({ label: `${acc.name} (${acc.bank_name})`, value: acc.id }))} 
              />
            </>
          )}
          <FormActions 
            onSave={savePaymentMethod} 
            onCancel={resetPaymentModal} 
            label={editingPaymentMethod ? t("btn_update_payment_method") : t("btn_link_method")} 
          />
        </Modal>
      )}

      {deleteAccountModal && (
        <Modal 
          title={t("title_delete_bank_account")} 
          onClose={() => !deletingAccount && setDeleteAccountModal(null)}
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-3.5">
              <div className="rounded-full bg-red-100 dark:bg-red-900/50 p-2 text-red-600 dark:text-red-400 shrink-0">
                <Icon name="alert" size={18} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-red-800 dark:text-red-300">
                  {t("warning_permanent_action")}
                </h4>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 leading-relaxed">
                  {t("confirm_delete_account", { name: deleteAccountModal.name || deleteAccountModal.bank_name })}
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-border dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-4 space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("label_display_name")}</span>
                <span className="text-sm font-bold text-ink dark:text-slate-100">{deleteAccountModal.name}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("col_bank")}</span>
                <span className="text-sm font-semibold text-ink dark:text-slate-200">{deleteAccountModal.bank_name}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("label_account_type")}</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                  {deleteAccountModal.account_type}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("label_account_number")}</span>
                <span className="text-xs font-mono font-semibold text-ink dark:text-slate-200">
                  •••• •••• {String(deleteAccountModal.account_number || '').slice(-4)}
                </span>
              </div>
              {deleteAccountModal.ifsc_code && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted font-medium">{t("label_ifsc")}</span>
                  <span className="text-xs font-mono font-semibold text-ink dark:text-slate-200">{deleteAccountModal.ifsc_code}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-xs pt-2 border-t border-border/60 dark:border-slate-800">
                <span className="text-muted font-medium">{t("col_balance") || "Balance"}</span>
                <span className="text-sm font-bold text-ink dark:text-slate-100">
                  Rs. {parseFloat(deleteAccountModal.current_balance || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-border dark:border-slate-800">
              <Btn 
                variant="default" 
                onClick={() => setDeleteAccountModal(null)} 
                disabled={deletingAccount}
              >
                {t("btn_cancel")}
              </Btn>
              <Btn 
                variant="solid_danger" 
                onClick={confirmDeleteAccount} 
                loading={deletingAccount} 
                icon="trash"
              >
                {t("btn_delete_account")}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {fdDetailModal && (
        <Modal 
          title={t("title_fd_details")} 
          onClose={() => setFdDetailModal(null)}
        >
          <div className="space-y-4">
            <div className="rounded-xl border border-border dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-4 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("label_display_name")}</span>
                <span className="text-sm font-bold text-ink dark:text-slate-100">{fdDetailModal.name}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("col_bank")}</span>
                <span className="text-sm font-semibold text-ink dark:text-slate-200">{fdDetailModal.bank_name}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("col_account_fd_no")}</span>
                <span className="text-xs font-mono font-semibold text-ink dark:text-slate-200">{fdDetailModal.account_number}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("label_branch")}</span>
                <span className="text-xs text-ink dark:text-slate-200">{fdDetailModal.branch || '-'}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("col_ifsc")}</span>
                <span className="text-xs font-mono text-ink dark:text-slate-200">{fdDetailModal.ifsc_code || '-'}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted font-medium">{t("col_status")}</span>
                <Badge color="blue">{t("badge_investment")}</Badge>
              </div>
              <div className="flex items-center justify-between text-xs pt-2.5 border-t border-border/60 dark:border-slate-800">
                <span className="text-muted font-medium">{t("col_principal_amount")}</span>
                <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                  Rs. {parseFloat(fdDetailModal.current_balance || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-border dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Btn 
                  sm 
                  icon="edit" 
                  onClick={() => {
                    const acc = fdDetailModal;
                    setFdDetailModal(null);
                    openEditModal(acc);
                  }}
                >
                  {t("btn_edit")}
                </Btn>
                <Btn 
                  sm 
                  variant="danger" 
                  icon="trash" 
                  onClick={() => {
                    const acc = fdDetailModal;
                    setFdDetailModal(null);
                    deleteAccount(acc);
                  }}
                >
                  {t("btn_delete")}
                </Btn>
              </div>
              <Btn variant="default" sm onClick={() => setFdDetailModal(null)}>
                {t("btn_cancel")}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {accounts.map(account => {
          const accent = ACCENT_CLASSES[account.card_accent || 'blue'];
          return (
            <div key={account.id} className={`group relative min-h-[210px] overflow-hidden rounded-[20px] ${accent.card} transition-all duration-300 hover:-translate-y-1 hover:scale-[1.01]`}>
              <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/5 blur-[1px]" />
              <div className="relative z-10 flex h-full min-h-[210px] flex-col px-[22px] py-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="mb-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white/40">{account.account_type.toUpperCase()} ACCOUNT</div>
                    <div className="text-sm font-extrabold tracking-[-0.01em] text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">{account.bank_name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {account.is_primary && (
                      <div className={`rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em] backdrop-blur ${accent.accent}`}>{t("lbl_primary")}</div>
                    )}
                    <button
                      onClick={() => deleteAccount(account)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1.5 text-sm font-semibold text-red-100 backdrop-blur transition-all hover:bg-red-500/20 hover:border-red-300/50 hover:text-white"
                    >
                      <Icon name="trash" size={11} />{t("btn_delete")}</button>
                  </div>
                </div>

                <div className="mb-3 flex items-center justify-between">
                  <ChipSVG color={accent.chip} />
                  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M5 12.55a11 11 0 0114.08 0" />
                    <path d="M1.42 9a16 16 0 0121.16 0" />
                    <path d="M8.53 16.11a6 6 0 016.95 0" />
                    <circle cx="12" cy="20" r="1" fill="rgba(255,255,255,0.35)" />
                  </svg>
                </div>

                <div className="mb-3.5">
                  <div className="mb-1 text-[9px] uppercase tracking-[0.1em] text-white/35">{t("lbl_current_balance")}</div>
                  <div className='flex gap-3 items-center'>
                    <div className="text-[26px] font-black leading-none tracking-[-0.03em] text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
                      {visibleBalanceId === account.id ? `Rs. ${parseFloat(account.current_balance).toLocaleString('en-IN')}` : maskBalance(account.current_balance)}
                    </div>
                    <button onClick={() => setVisibleBalanceId(current => current === account.id ? null : account.id)} className="font-semibold text-white backdrop-blur transition-colors">
                      {visibleBalanceId === account.id ? <Icon name="eyeOff" size={16} /> : <Icon name="eye" size={20} />}
                    </button>
                  </div>
                </div>

                <div className="mt-auto mb-3 h-px bg-white/10" />

                <div className="mb-3 flex items-end justify-between gap-3">
                  <div>
                    <div className="mb-1 text-[8px] uppercase tracking-[0.1em] text-white/30">{t("lbl_account_no")}</div>
                    <div className="font-mono text-[13px] font-bold tracking-[0.15em] text-white/75">**** **** {account.account_number.slice(-4)}</div>
                  </div>
                  <div className="text-right">
                    <div className="mb-0.5 text-[8px] uppercase tracking-[0.1em] text-white/30">{t("lbl_ifsc_branch")}</div>
                    <div className="text-sm font-bold tracking-[0.04em] text-white/60">{account.ifsc_code || '-'}</div>
                    <div className="mt-0.5 text-[9px] text-white/30">{account.branch}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => openEditModal(account)} className="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white/85 backdrop-blur transition-colors hover:bg-white/20">{t("btn_edit")}</button>
                  <button onClick={() => { setStmtErrors({}); setStmtForm({ files: [], password: '' }); setStmtModal(account); }} className="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white/85 backdrop-blur transition-colors hover:bg-white/20">{t("btn_upload_statement")}</button>
                  <button onClick={() => fetchTransactions(account)} className="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white/85 backdrop-blur transition-colors hover:bg-white/20">{t("btn_transactions")}</button>
                  <button onClick={() => fetchStatements(account)} className="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white/85 backdrop-blur transition-colors hover:bg-white/20">Statements</button>
                  <button onClick={() => openLinkMethodModal(account)} className="col-span-2 rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white/85 backdrop-blur transition-colors hover:bg-white/20">{t("btn_link_method")}</button>
                </div>
              </div>
            </div>
          );
        })}

        {accounts.length < 6 && (
          <div onClick={openCreateModal} className="flex min-h-[210px] cursor-pointer flex-col items-center justify-center rounded-xl2 border-2 border-dashed border-slate-200 p-8 text-muted transition-all hover:border-primary hover:bg-primary/5 hover:text-primary dark:text-white dark:border-slate-700">
            <div className="mb-3 flex h-12 w-12 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 items-center justify-center rounded-full  transition-colors group-hover:bg-primary/10 dark:text-white dark:border-slate-700">
              <Icon name="plus" size={24} />
            </div>
            <span className="text-sm font-bold">{t("title_add_new_account")}</span>
            <span className="mt-1 text-xs opacity-60">{t("subtitle_add_new_account")}</span>
          </div>
        )}
      </div>

      <Card>
        <SectionHeader title={t("title_linked_payment_methods")} subtitle={t("subtitle_linked_payment_methods")} />
        <DataTable
          cols={[t("col_type"), t("col_id_number"), t("col_linked_account"), t("col_status"), t("col_actions")]}
          rows={accounts.flatMap(account => {
            const paymentMethods = account.paymentMethods || account.payment_methods || [];
            return paymentMethods.map(method => [
              method.type,
              <span className="font-mono text-xs">{method.identifier}</span>,
              account.name,
              <Badge color={(method.status || 'inactive') === 'active' ? 'green' : 'red'} dot>{(method.status || 'inactive').charAt(0).toUpperCase() + (method.status || 'inactive').slice(1)}</Badge>,
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Btn xs icon="edit" onClick={() => openEditPaymentModal(method, account)}>{t("btn_edit")}</Btn>
                <Btn xs variant="danger" icon="trash" onClick={() => deletePaymentMethod(method)}>{t("btn_delete")}</Btn>
              </div>,
            ]);
          })}
        />
        {accounts.length > 0 && accounts.every(account => ((account.paymentMethods || account.payment_methods || []).length === 0)) && (
          <p className="py-6 text-center text-sm text-muted">{t("msg_no_payment_methods")}</p>
        )}
        <div className="mt-4">
          <Alert type="info">{t("alert_role_based_access")}</Alert>
        </div>
      </Card>

      <Card>
        <SectionHeader title={t("title_fd_investments")} subtitle={t("subtitle_fd_investments")} action={<Btn sm variant="primary" icon="plus" onClick={() => { setForm(current => ({ ...current, account_type: 'fd', card_accent: 'amber' })); setAccountModalOpen(true); }}>{t("btn_add_fd")}</Btn>} />
        {accounts.filter(account => account.account_type.toLowerCase() === 'fd').length === 0 ? (
          <p className="py-6 text-center text-sm text-muted">{t("msg_no_fd_accounts")}</p>
        ) : (
          <DataTable
            cols={[t("col_bank"), t("col_account_fd_no"), t("col_principal_amount"), t("col_ifsc"), t("col_status"), t("col_actions")]}
            rows={accounts.filter(account => account.account_type.toLowerCase() === 'fd').map(account => [
              account.bank_name,
              <span className="font-mono text-xs font-semibold">{account.account_number}</span>,
              `Rs. ${parseFloat(account.current_balance).toLocaleString('en-IN')}`,
              account.ifsc_code || '-',
              <Badge color="blue">{t("badge_investment")}</Badge>,
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Btn xs icon="eye" onClick={() => setFdDetailModal(account)}>{t("btn_details")}</Btn>
                <Btn xs icon="edit" onClick={() => openEditModal(account)}>{t("btn_edit")}</Btn>
                <Btn xs variant="danger" icon="trash" onClick={() => deleteAccount(account)}>{t("btn_delete")}</Btn>
              </div>,
            ])}
          />
        )}
      </Card>
    </div>
  );
}
